<template>
  <div class="individual-sports-container">
    <div class="results-card">
      <!-- Results Header -->
      <div class="results-header">
        <h3 class="results-title">{{ getResultsTitle() }}</h3>
        <span class="participant-count">{{ getParticipantCount() }} {{ getParticipantCount() === 1 ? 'participant' : 'participants' }}</span>
      </div>

      <!-- Individual Participants Results -->
      <div v-if="participants.length > 0" class="results-table" :class="getTableClass()">
        <div class="table-header">
          <div class="col-position">POS</div>
          <div class="col-athlete">ATHLETE</div>
          <div class="col-result">{{ getResultColumnHeader().toUpperCase() }}</div>
        </div>

        <div class="table-body">
          <div
            class="table-row"
            v-for="participant in sortedParticipants"
            :key="participant.id"
            :class="getRowClass(participant.position)"
          >
            <div class="col-position">
              <span class="position-badge" :class="getPositionClass(participant.position)">
                {{ participant.position || participant.number || '-' }}
              </span>
            </div>
            <div class="col-athlete">
              <span class="athlete-name">{{ getParticipantName(participant) }}</span>
            </div>
            <div class="col-result">
              <span class="result-value" :class="getResultClass(participant)">
                {{ getResultValue(participant) }}{{ getResultUnit() }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Head-to-Head Matches Results -->
      <div v-else-if="hasMatches" class="head-to-head-results">
        <div class="matches-list">
          <div
            class="match-result"
            v-for="match in matches"
            :key="match.id"
          >
            <div class="match-participants">
              <div class="participant" :class="{ winner: getWinner(match) === 'team1' }">
                <span class="participant-name">{{ getParticipantName(match.team1) }}</span>
                <span class="participant-score">{{ match.team1.score || 0 }}</span>
              </div>

              <div class="vs-divider">VS</div>

              <div class="participant" :class="{ winner: getWinner(match) === 'team2' }">
                <span class="participant-name">{{ getParticipantName(match.team2) }}</span>
                <span class="participant-score">{{ match.team2.score || 0 }}</span>
              </div>
            </div>

            <div v-if="match.result" class="match-outcome">
              {{ getMatchOutcome(match) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-results">
        <p>No results available</p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'IndividualSportsResults',
  props: {
    participants: {
      type: Array,
      required: true,
      default: () => []
    },
    scoringType: {
      type: String,
      required: true,
      validator: value => ['time', 'measurement', 'points'].includes(value)
    },
    sportType: {
      type: String,
      default: 'Individual'
    },
    matches: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    sortedParticipants() {
      return [...this.participants].sort((a, b) => {
        if (a.position && b.position) {
          return parseInt(a.position) - parseInt(b.position);
        }
        if (a.position && !b.position) return -1;
        if (!a.position && b.position) return 1;
        return 0;
      });
    },

    // Add computed property for head-to-head matches if no participants
    hasMatches() {
      return this.matches && this.matches.length > 0;
    }
  },
  methods: {
    getParticipantName(participant) {
      if (participant.member && participant.member.profile) {
        return `${participant.member.profile.firstName} ${participant.member.profile.lastName}`;
      }
      if (participant.member) {
        return `${participant.member.firstName || ''} ${participant.member.lastName || ''}`.trim() ||
               participant.member.name || 'Unknown Member';
      }
      return participant.name || 'Unknown';
    },

    getResultsTitle() {
      const sportMap = {
        time: 'Race Results',
        measurement: 'Field Event Results',
        points: 'Competition Results'
      };
      return sportMap[this.scoringType] || 'Event Results';
    },

    getScoringType() {
      const typeMap = {
        time: 'Time-based',
        measurement: 'Distance/Height',
        points: 'Points-based'
      };
      return typeMap[this.scoringType] || 'Competition';
    },

    getScoringClass() {
      return `scoring-${this.scoringType}`;
    },

    getTableClass() {
      return `table-${this.scoringType}`;
    },

    getResultColumnHeader() {
      const headerMap = {
        time: 'Time',
        measurement: 'Distance/Height',
        points: 'Score'
      };
      return headerMap[this.scoringType] || 'Result';
    },

    getResultValue(participant) {
      switch (this.scoringType) {
      case 'time':
        return participant.time || '--:--';
      case 'measurement':
        return participant.measurement || '--';
      case 'points':
        return participant.points || '-';
      default:
        return '-';
      }
    },

    getResultUnit() {
      if (this.scoringType === 'measurement') return 'm';
      return '';
    },

    getResultClass(participant) {
      const hasResult = this.getResultValue(participant) !== '--:--' &&
                       this.getResultValue(participant) !== '--' &&
                       this.getResultValue(participant) !== '-';
      return hasResult ? 'has-result' : 'no-result';
    },

    getPositionClass(position) {
      if (position === 1) return 'gold';
      if (position === 2) return 'silver';
      if (position === 3) return 'bronze';
      return 'default';
    },

    getRowClass(position) {
      if (position <= 3) return `podium-${position}`;
      return '';
    },

    getParticipantCount() {
      return this.participants.length;
    },

    getWinner(match) {
      if (match.team1.score > match.team2.score) return 'team1';
      if (match.team2.score > match.team1.score) return 'team2';
      return null;
    },

    getMatchOutcome(match) {
      if (match.team1.score > match.team2.score) return 'Team 1 Wins';
      if (match.team2.score > match.team1.score) return 'Team 2 Wins';
      return 'Draw';
    }
  }
});
</script>

<style lang="scss" scoped>
// Dark Theme Variables
$background: #0f1419;
$card-bg: linear-gradient(135deg, rgba(20, 20, 22, 0.6) 0%, rgba(15, 15, 17, 0.4) 100%);
$accent-color: #4caf50;
$text-primary: rgba(255, 255, 255, 0.95);
$text-secondary: rgba(255, 255, 255, 0.7);
$text-light: rgba(255, 255, 255, 0.5);
$border-color: rgba(255, 255, 255, 0.1);

.individual-sports-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem;
}

.results-card {
  background: $card-bg;
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border: 1px solid $border-color;
  border-radius: 16px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid $border-color;
  background: rgba(0, 0, 0, 0.2);

  .results-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: $text-primary;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  .participant-count {
    color: $text-secondary;
    font-size: 0.75rem;
    font-weight: 400;
  }
}

.results-table {
  .table-header {
    display: grid;
    grid-template-columns: 50px 1fr 100px;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid $border-color;
    font-weight: 500;
    color: $text-light;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    align-items: center;
    background: rgba(255, 255, 255, 0.02);
  }

  .table-body {
    .table-row {
      display: grid;
      grid-template-columns: 50px 1fr 100px;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      transition: background 0.1s ease;
      align-items: center;

      &:hover {
        background: rgba(255, 255, 255, 0.03);
      }

      &.podium-1 {
        background: rgba(245, 158, 11, 0.1);
        border-left: 2px solid #f59e0b;
      }

      &.podium-2 {
        background: rgba(156, 163, 175, 0.1);
        border-left: 2px solid #9ca3af;
      }

      &.podium-3 {
        background: rgba(234, 88, 12, 0.1);
        border-left: 2px solid #ea580c;
      }

      &:last-child {
        border-bottom: none;
      }
    }
  }
}

.col-position {
  .position-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.8rem;

    &.gold {
      background: #f59e0b;
      color: white;
    }

    &.silver {
      background: #9ca3af;
      color: white;
    }

    &.bronze {
      background: #ea580c;
      color: white;
    }

    &.default {
      background: rgba(255, 255, 255, 0.1);
      color: $text-secondary;
    }
  }
}

.col-athlete {
  .athlete-name {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: $text-primary;
    line-height: 1.2;
  }
}

.col-result {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;

  .result-value {
    font-size: 1rem;
    font-weight: 600;

    &.has-result {
      color: $text-primary;
    }

    &.no-result {
      color: $text-light;
    }
  }

  .result-unit {
    font-size: 0.75rem;
    color: $text-secondary;
    font-weight: 400;
  }
}

.head-to-head-results {
  padding: 1rem;

  .matches-list {
    .match-result {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);

      &:last-child {
        border-bottom: none;
      }

      .match-participants {
        display: flex;
        align-items: center;
        gap: 1rem;

        .participant {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;

          &.winner {
            color: $accent-color;
            font-weight: 600;
          }

          .participant-name {
            font-size: 0.85rem;
            font-weight: 500;
            color: $text-primary;
          }

          .participant-score {
            font-size: 1rem;
            font-weight: 600;
            color: $text-primary;
          }
        }
      }

      .vs-divider {
        font-size: 0.75rem;
        font-weight: 500;
        color: $text-light;
        padding: 0.25rem 0.5rem;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 4px;
      }

      .match-outcome {
        font-size: 0.75rem;
        font-weight: 500;
        color: $text-secondary;
      }
    }
  }
}

.empty-results {
  padding: 2rem;
  text-align: center;
  color: $text-light;
  font-size: 0.875rem;
}

// Mobile Responsiveness
@media (max-width: 768px) {
  .individual-sports-container {
    padding: 0.25rem;
  }

  .results-header {
    padding: 0.75rem 1rem;

    .results-title {
      font-size: 1rem;
    }

    .participant-count {
      font-size: 0.7rem;
    }
  }

  .results-table {
    .table-header {
      grid-template-columns: 40px 1fr 80px;
      padding: 0.5rem 0.75rem;
      gap: 0.5rem;
      font-size: 0.65rem;
    }

    .table-body .table-row {
      grid-template-columns: 40px 1fr 80px;
      padding: 0.5rem 0.75rem;
      gap: 0.5rem;
    }
  }

  .col-position .position-badge {
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.7rem;
  }

  .col-athlete .athlete-name {
    font-size: 0.8rem;
  }

  .col-result .result-value {
    font-size: 0.9rem;
  }

  .head-to-head-results {
    padding: 0.75rem;

    .matches-list .match-result {
      flex-direction: column;
      gap: 0.5rem;
      align-items: flex-start;

      .match-participants {
        gap: 0.75rem;

        .participant {
          .participant-name {
            font-size: 0.8rem;
          }

          .participant-score {
            font-size: 0.9rem;
          }
        }
      }

      .vs-divider {
        font-size: 0.7rem;
      }

      .match-outcome {
        font-size: 0.7rem;
      }
    }
  }
}
</style>